import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loginUser } from "../services/api";
import axios from "axios";
import { computeIsCreator } from "../utils/onboarding";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Alert, AlertDescription } from "../components/ui/alert";

// 🎯 Calcular destino de redirección tras login/sesión activa
const getDestino = (email, fromPathname) =>
  fromPathname ||
  (email === "jarenas@previonix.com" ? "/adminDashboard" : "/dashboard");

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const fromPathname = location.state?.from?.pathname || null;
  const API_URL = process.env.REACT_APP_API_URL;

  // 🧹 Función para limpiar tokens y cookies
  const clearSession = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("servicioActivo");
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
  };

  // 🧠 Verificar sesión activa al cargar la página
  useEffect(() => {
    const checkSession = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const res = await axios.get(`${API_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data) {
          const destino = getDestino(res.data.email, fromPathname);
          navigate(destino, { replace: true });
        }
      } catch {
        clearSession(); // limpiar si el token no es válido
      }
    };

    checkSession();
  }, [navigate, API_URL, fromPathname]);

  // 🔐 Iniciar sesión
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    clearSession(); // limpiar tokens/cookies viejas antes del login

    try {
      // Login
      const data = await loginUser(email, password);
      const token = data.token;
      const userFromLogin = data.user;

      // ⚠ Validar longitud del token antes de guardar
      if (token.length > 4000) {
        throw new Error(
          "Token demasiado grande. Contacta al administrador del sistema.",
        );
      }

      localStorage.setItem("token", token);

      // Obtener usuario completo desde /me
      let userFull;
      try {
        const resMe = await axios.get(`${API_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        userFull = resMe.data;
      } catch {
        userFull = userFromLogin; // fallback
      }

      // 🧹 Guardar versión ligera del user
      const userLite = {
        id: userFull._id,
        name: userFull.name,
        email: userFull.email,
        role: userFull.role,
        empresa: userFull.empresa,
        foto: userFull.foto || null, // <-- ¡Aquí está el fix!
        onboarding: userFull.onboarding || {},
        dashboardLayout: userFull.dashboardLayout || {},
        isCreator: computeIsCreator(userFull),
        emailVerified: userFull.emailVerified,
        policyAcceptance: userFull.policyAcceptance || null,
      };
      localStorage.setItem("user", JSON.stringify(userLite));
      window.dispatchEvent(new Event("user-sync"));

      const destino = getDestino(userFull.email, fromPathname);
      navigate(destino, { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Error de autenticación",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative bg-gray-900 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center filter brightness-50"
        style={{ backgroundImage: "url('/login-bg.jpg')" }}
      />
      <div className="relative z-10 w-full max-w-md mx-4 p-10 bg-white/90 rounded-3xl shadow-2xl backdrop-blur-md animate-fadeIn">
        <div className="flex justify-center mb-6">
          <img
            src="/logo.png"
            alt="Previonix Logo"
            className="h-16 w-auto object-contain"
          />
        </div>
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
          Inicia sesión
        </h2>
        <p className="text-center text-gray-600 mb-8">
          Ingresa tus credenciales para continuar
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              placeholder="Correo electrónico"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition bg-white/90"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition bg-white/90"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="text-right">
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              onClick={() => navigate("/forgot-password")}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <Button
            type="submit"
            className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition"
            disabled={loading}
          >
            {loading ? "Validando..." : "Ingresar"}
          </Button>
        </form>

        <div className="text-center mt-6">
          <button
            className="text-blue-600 hover:text-blue-800 font-semibold"
            onClick={() => navigate("/register")}
          >
            ¿No tienes cuenta? Regístrate aquí
          </button>
        </div>

        {error && (
          <Alert type="error" className="mt-5">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from {opacity: 0; transform: translateY(-10px);} to {opacity: 1; transform: translateY(0);} }
        .animate-fadeIn { animation: fadeIn 0.5s ease forwards; }
      `}</style>
    </div>
  );
};

export default LoginPage;
